/**
 * Quiz Revision Service for creating AI-powered quiz revision conversations
 */

import { defaultErrorHandler as commonErrorHandler, createHeaders as createCommonHeaders, getApiUrls } from './serviceConfig';

/**
 * Creates a configured quiz revision service
 * @param {Object} config - Configuration options
 * @param {string} config.baseUrl - Base URL for the API
 * @param {string} config.environment - Environment ('development' or 'production')
 * @param {Object} config.headers - Additional headers to include in all requests
 * @param {Function} config.onError - Global error handler
 * @param {Function} config.getAuthToken - Function to get the latest auth token (preferred)
 * @param {string} config.authToken - Static authentication token for API requests (not recommended)
 * @returns {Object} The quiz revision service API
 */
export function createQuizRevisionService(config = {}) {
  const DEFAULT_HEADERS = config.headers || {};
  const defaultErrorHandler = config.onError || ((error) => commonErrorHandler('Quiz Revision service', error));
  // Use getAuthToken function if provided, or use static token as fallback
  const getAuthToken = config.getAuthToken || (() => config.authToken);

  /**
   * Helper to create request headers
   * @param {Object} additionalHeaders - Additional headers to include
   * @returns {Object} Headers object
   */
  const createHeaders = (additionalHeaders = {}) => {
    // Only pass custom auth getter if one was provided to the service
    const customAuthGetter = config.getAuthToken ? getAuthToken : null;
    return createCommonHeaders(
      {
        ...DEFAULT_HEADERS,
        ...additionalHeaders,
      },
      customAuthGetter
    );
  };

  /**
   * Helper to handle API errors
   * @param {Response} response - Fetch Response object
   * @param {string} errorPrefix - Prefix for error message
   * @returns {Promise} JSON response or throws error
   */
  const handleResponse = async (response, errorPrefix = 'API error') => {
    if (!response.ok) {
      let errorMessage = `${errorPrefix}: ${response.status}`;

      try {
        const errorData = await response.json();
        if (errorData.message) {
          errorMessage = `${errorPrefix}: ${errorData.message}`;
        }
      } catch {
        // If we can't parse the error JSON, just use the status code
      }

      const error = new Error(errorMessage);
      error.statusCode = response.status;
      throw error;
    }

    return response.json();
  };

  return {
    /**
     * Create a quiz revision conversation
     * @param {Object} payload - Complete API payload
     * @param {string} payload.externalUserId - External user ID
     * @param {string} payload.quizJobGuid - Quiz job GUID
     * @param {string} payload.questionReferenceId - Question reference ID
     * @param {Object} payload.quizQuestionData - Complete quiz question metadata
     * @param {Object} payload.learningObjectives - Learning objectives (optional)
     * @returns {Promise<{conversationId: string}>}
     */
    createQuizRevisionConversation: async (payload) => {
      try {
        const apiUrls = getApiUrls();
        const url = apiUrls.conversations.quizRevision();

        const response = await fetch(url, {
          method: 'POST',
          headers: createHeaders(),
          body: JSON.stringify(payload),
        });

        return await handleResponse(response, 'Failed to create quiz revision conversation');
      } catch (error) {
        console.error('[quizRevisionService] Error creating conversation:', error);
        defaultErrorHandler(error);
        throw error;
      }
    },
  };
}

// Create default service instance
const quizRevisionService = createQuizRevisionService();

// Export default service instance
export default quizRevisionService;

// Export the main function directly for convenience
export const createQuizRevisionConversation = quizRevisionService.createQuizRevisionConversation;
